import React, { useState } from 'react';
import { useIdentity } from './IdentityContext';
import { useConfirm } from './ConfirmContext';
import { useToast } from './ToastContext';
import Spinner from './Spinner';
import { ImageThumb, UploadButton, fileToBase64, validateFiles } from './ImageUploadControls';
import { categoryImageUrl } from '../data/categoryImageUrl';
import { GOLD_LT, CREAM } from '../theme';

// Uploaded one at a time (not in parallel) — each upload assigns its `order`
// based on the server's current image count for this dress, so concurrent
// requests could race and collide on order (mirrors ImageManager.js).
export default function DressImageManager({ categorySlug, dress, onChange }) {
  const { authFetch } = useIdentity();
  const confirm = useConfirm();
  const toast = useToast();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [error, setError] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  const images = (dress.images || []).slice().sort((a, b) => a.order - b.order);

  const applyDoc = (doc) => {
    const category = doc.categories.find((c) => c.slug === categorySlug);
    const updated = category && category.dresses.find((d) => d.slug === dress.slug);
    if (updated) onChange(updated);
  };

  const upload = async (files) => {
    setError(null);
    const valid = validateFiles(files, setError);
    if (!valid.length) return;

    setUploading(true);
    try {
      for (let i = 0; i < valid.length; i++) {
        const file = valid[i];
        setUploadProgress(valid.length > 1 ? `${i + 1}/${valid.length}` : null);
        const dataBase64 = await fileToBase64(file);
        const res = await authFetch('/.netlify/functions/categories-images', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ categorySlug, dressSlug: dress.slug, kind: 'dress', contentType: file.type, dataBase64 }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Upload failed');
        applyDoc(data.doc);
      }
      toast.success(valid.length > 1 ? 'Images uploaded.' : 'Image uploaded.');
    } catch (err) {
      setError(err.message);
      toast.error(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const remove = async (image) => {
    const ok = await confirm('Remove this image from the draft?');
    if (!ok) return;
    setRemovingId(image.id);
    try {
      const res = await authFetch('/.netlify/functions/categories-images', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ categorySlug, dressSlug: dress.slug, imageId: image.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to remove image');
      applyDoc(data.doc);
      toast.success('Image removed.');
    } catch (err) {
      toast.error(err.message || 'Failed to remove image.');
    } finally {
      setRemovingId(null);
    }
  };

  const reorder = async (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= images.length) return;
    const reordered = [...images];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    const withOrder = reordered.map((img, i) => ({ ...img, order: i }));
    try {
      const res = await authFetch('/.netlify/functions/categories-dresses', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ categorySlug, dressSlug: dress.slug, images: withOrder }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to reorder images');
      onChange(data.dress);
    } catch (err) {
      toast.error(err.message || 'Failed to reorder images.');
    }
  };

  return (
    <div>
      <div style={{ fontSize: 12, letterSpacing: '.14em', textTransform: 'uppercase', color: GOLD_LT, marginBottom: 8 }}>Images</div>
      {error && <p style={{ color: '#e08a8a', fontSize: 13 }}>{error}</p>}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 14 }}>
        {images.map((img, i) => (
          <div key={img.id} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <ImageThumb image={img} size={92} urlFn={categoryImageUrl} />
            <div style={{ display: 'flex', gap: 4, justifyContent: 'center', alignItems: 'center' }}>
              <button onClick={() => reorder(i, i - 1)} disabled={i === 0} style={{ background: 'none', border: 'none', color: CREAM, cursor: i === 0 ? 'default' : 'pointer', opacity: i === 0 ? .3 : 1, fontSize: 14 }}>←</button>
              {removingId === img.id ? (
                <Spinner size={11} />
              ) : (
                <button onClick={() => remove(img)} disabled={removingId != null} style={{ background: 'none', border: 'none', color: '#e08a8a', cursor: removingId != null ? 'default' : 'pointer', opacity: removingId != null ? .4 : 1, fontSize: 14 }}>×</button>
              )}
              <button onClick={() => reorder(i, i + 1)} disabled={i === images.length - 1} style={{ background: 'none', border: 'none', color: CREAM, cursor: i === images.length - 1 ? 'default' : 'pointer', opacity: i === images.length - 1 ? .3 : 1, fontSize: 14 }}>→</button>
            </div>
          </div>
        ))}
      </div>

      <UploadButton label="+ Add images" uploading={uploading} progress={uploadProgress} multiple onUpload={upload} />
      {images.length === 0 && <p style={{ marginTop: 10, fontSize: 12, color: '#e0b86b' }}>At least one image is required before this piece can be published.</p>}
    </div>
  );
}
